import React, { useContext, useMemo } from "react"
import { TContentfulImage } from "../atoms/breakpoint-image"
import { Grid } from "../atoms/layout-primitives/grid"
import { UIContext } from "../../components/shared/layout"
import Block from "../atoms/layout-primitives/block"
import { graphql } from "gatsby"
import Slider from "react-slick"
import Img from "gatsby-image"
import VariantContainer from "../atoms/variant-container"
import { css } from "styled-components"

export interface ILogoGrid {
  __typename: typeof LOGO_GRID_TYPENAME
  logos: TContentfulImage[]
}

const LogoGrid = ({ logos }: Omit<ILogoGrid, "__typename">) => {
  const {
    //@ts-ignore - these props are correct but UIContext has no default to infer from
    state: { breakpoint, clientLoaded },
  } = useContext(UIContext)

  // On mobile the logos are grouped into 10s and placed on cards
  const groupSize = 10
  const logoGroups = useMemo(() => {
    const arrays = []
    for (let i = 0; i < logos.length; i += groupSize) {
      arrays.push(logos.slice(i, i + groupSize))
    }
    return arrays
  }, [logos, groupSize])

  return (
    <Grid>
      <Block
        variants={[
          css`
            background: var(--white);
            box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
            border-radius: 2px;
            padding: 32px 32px 72px 32px;

            .slick-dots {
              bottom: -48px;
            }
          `,
          css`
            padding: 32px;
            width: max-content;
            margin: 0 auto;
          `,
          css`
            padding: 64px;
          `,
        ]}
      >
        {breakpoint === "sm" ? (
          clientLoaded && (
            <Slider dots arrows={false}>
              {logoGroups.map((group, index) => (
                <div key={index}>
                  <VariantContainer
                    variants={[
                      css`
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        grid-template-rows: repeat(${groupSize / 2}, 1fr);
                        justify-content: center;
                        justify-items: center;

                        gap: 24px 32px;
                      `,
                    ]}
                  >
                    {group.map((logo, index) => (
                      <div
                        style={{
                          width: "120px",
                        }}
                        key={index}
                      >
                        <Img
                          fluid={logo.fluid}
                          alt={logo.description}
                          imgStyle={{ objectFit: "contain" }}
                        />
                      </div>
                    ))}
                  </VariantContainer>
                </div>
              ))}
            </Slider>
          )
        ) : (
          <VariantContainer
            variants={[
              css`
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                justify-content: center;
                justify-items: center;
                gap: 32px 48px;
              `,
              null,
              css`
                grid-template-columns: repeat(6, minmax(0, 1fr));
              `,
            ]}
          >
            {logos.map((logo, index) => (
              <VariantContainer
                key={index}
                variants={[
                  css`
                    min-width: 120px;
                  `,
                  null,
                  css`
                    min-width: 140px;
                  `,
                ]}
              >
                <Img
                  fluid={logo.fluid}
                  alt={logo.description}
                  imgStyle={{ objectFit: "contain" }}
                />
              </VariantContainer>
            ))}
          </VariantContainer>
        )}
      </Block>
    </Grid>
  )
}

export const LogoGridFragment = graphql`
  fragment ContentfulLogoGrid on ContentfulLogoGrid {
    contentful_id
    logos {
      fluid(quality: 90) {
        ...GatsbyContentfulFluid_withWebp
      }
      description
    }
  }
`
export const LOGO_GRID_TYPENAME = "ContentfulLogoGrid"
export default LogoGrid
